import type { FC } from 'react';
import { MultiThreadingWrapper } from './MultiThreading.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import multithreading from './../../../resources/content/multithreading.png';

interface MultiThreadingProps { }

const MultiThreading: FC<MultiThreadingProps> = () => (
   <MultiThreadingWrapper>
      <header className="entry-header ">
         <h1 className="entry-title" itemProp="headline">Multi Threading</h1>
      </header>
      <br/>
      <div className="entry-content clear" itemProp="text" style={{ height: 'auto !important' }}>
         <p className="text-align-justify">Thread is a lightweight smallest unit of processing. Java uses threads by using a “Thread Class”. </p>
         <br/>
         <h4 className="text-align-justify"><strong>Types of thread</strong></h4>
         <ul className="text-align-justify">
            <li><strong>User thread :</strong> The one that is created when an application begins.</li>
            <li><strong>Daemon thread :</strong> The ones that are used when we want to clean the application and are used in the background.</li>
         </ul>
         <br/>
         <p className="text-align-justify">Multithreading is a process of executing two or more threads simultaneously to maximum utilization of CPU. Hence, it is also known as Concurrency in Java.<br /></p>
         <h4 className="text-align-justify"><strong>Advantages of Multithreading</strong></h4>
         <ul className="text-align-justify">
            <li>Multiple threads don’t allocate separate memory area, hence they save memory.</li>
            <li>Context switching between threads takes less time.</li>
            <li>Threads are independent, so operation on one thread won’t affect another. If an exception occurs in one thread, it doesn’t affect other threads.</li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>Life cycle of a thread</strong></h4>
         <figure className="aligncenter size-large is-style-default">
            <img decoding="async" width="585" height="486" src={multithreading} alt="" className="lazy-loaded" sizes="(max-width: 585px) 100vw, 585px" />
         </figure>
         <ul className="text-align-justify">
            <li><strong>New</strong>
               <ul>
                  <li>In this phase, a new thread is created using class the “Thread class”.</li>
                  <li>It remains in this state until the program starts the thread. </li>
                  <li>It is also known as a born thread.</li>
               </ul>
            </li>
            <li><strong>Runnable</strong>
               <ul>
                  <li>In this phase, the instance of the thread is invoked with a start method. </li>
                  <li>The thread control goes to the scheduler to finish the execution. </li>
                  <li>It depends on the scheduler, whether to run the thread.</li>
               </ul>
            </li>
            <li><strong>Running </strong>
               <ul>
                  <li>When the thread starts executing, then the state is changed to a “running” state. </li>
                  <li>After that, the scheduler will select one thread from the thread pool, and it starts executing in the application.</li>
               </ul>
            </li>
            <li><strong>Waiting </strong>
               <ul>
                  <li>In this state, a thread has to wait. </li>
                  <li>As multiple threads are running in the application, there is a need for synchronization between threads. Therefore, one thread has to wait, until the other thread gets executed.</li>
               </ul>
            </li>
            <li><strong>Dead </strong>
               <ul>
                  <li>This is the state when the thread is terminated. </li>
                  <li>The thread is in a running state and as soon as it completed processing it is in “dead state”.</li>
               </ul>
            </li>
         </ul>
         <br/>
         <h4  className="text-align-justify"><strong>Mechanisms to create a thread </strong></h4>
         <ul  className="text-align-justify">
            <li><strong>Extending the Thread class </strong>
               <ul>
                  <li>Create a class that extends the Thread class.</li>
                  <li>Inside class override the run() method available in the Thread class. A thread begins its life inside a run() method. </li>
                  <li>Now create an object of this class and call the start() method to start the execution of a thread.</li>
                  <li>Start() invokes the run() method on the Thread object.</li>
                  <li>The class cannot extend any other class if we extend the Thread class because Java doesn’t support multiple inheritance.</li>
                  <li>Thread class provides some inbuilt methods like yield(), interrupt(), setPriority(), etc.</li>
               </ul>
            </li>
            <li><strong>Implementing the Runnable Interface </strong>
               <ul>
                  <li>Create a new class that implements java.lang.Runnable interface.</li>
                  <li>This class overrides the run() method available in the interface.</li>
                  <li>After that,  instantiate the Thread object and call the start() method on this object.</li>
               </ul>
            </li>
         </ul>
         <br/>
         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               '//Extending the Thread class\n' +
               '\n' +
               'public class MultithreadingClass extends Thread {\n' +
               '  public void run() {\n' +
               '    try {\n' +
               '      //Fetching the thread that is running\n' +
               '      Thread.currentThread().getId();\n' +
               '    } catch (Exception e) {\n' +
               '      System.out.println("Exception caught");\n' +
               '    }\n' +
               '  }\n' +
               '}\n' +
               '\n' +
               'public class Multithread {\n' +
               '  public static void main(String args[]) {\n' +
               '    int numberOfThreads = 10;\n' +
               '    for (int i = 0; i<numberOfThreads; i++) {\n' +
               '      //calling start method\n' +
               '      MultithreadingClass mtClass = new MultithreadingClass();\n' +
               '      mtClass.start();\n' +
               '    }\n' +
               '  }\n' +
               '}'
            }
         </SyntaxHighlighter>
         <br/>
         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               '//Implementing the Runnable Interface\n' +
               '\n' +
               'public class MultithreadingClass implements Runnable {\n' +
               '  public void run() {\n' +
               '    try {\n' +
               '      //Fetching the thread that is running\n' +
               '      Thread.currentThread().getId();\n' +
               '    } catch (Exception e) {\n' +
               '      System.out.println("Exception caught);\n' +
               '      }\n' +
               '    }\n' +
               '  }\n' +
               '\n' +
               '  public class Multithread {\n' +
               '    public static void main(String args[]) {\n' +
               '      int numberOfThreads = 10;\n' +
               '      for (int i = 0; i<numberOfThreads; i++) {\n' +
               '        //calling start method\n' +
               '        Thread mtClass = new Thread(new MultithreadingClass());\n' +
               '        mtClass.start();\n' +
               '      }\n' +
               '    }\n' +
               '  }'
            }
         </SyntaxHighlighter>
      </div>
   </MultiThreadingWrapper>
);

export default MultiThreading;
